import { and, eq } from 'ember-truth-helpers';
import { contactIdentityKey } from '../utils/messaging/contact-identity';
import { fn } from '@ember/helper';
import { noop } from 'ember-composable-helpers/helpers/noop';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Tag from 'ember-simplepractice/components/spds/indicator/tag';
import TextInput from 'ember-simplepractice/components/spds/input/text';
import styles from './contact-search.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const selectionKeys = ['Backspace', 'Enter', ' '];

export default class ContactSearch extends Component {
  @service intl;
  @service messaging;

  @tracked highlightedContactId = null;

  get searchPlaceholderText() {
    const partOne = this.intl.t('emc.contacts_page.search_prefix');
    const partTwo = this.messaging.searchableContactsText;
    return this.args.selectedContacts?.length ? '' : `${partOne} ${partTwo}`;
  }

  get selectedContacts() {
    return this.args.selectedContacts ?? [];
  }

  get lastContact() {
    const contacts = this.selectedContacts;
    return contacts.length ? contacts[contacts.length - 1] : null;
  }

  get isSingleSelect() {
    return !this.args.multiSelect;
  }

  get lockedContactIds() {
    return this.args.lockedContactIds ?? [];
  }

  isLocked = contact => {
    if (!contact) return false;
    const key = contactIdentityKey(contact);
    const lockedIds = this.lockedContactIds;
    return typeof lockedIds.has === 'function' ? lockedIds.has(key) : lockedIds.includes(key);
  };

  onInputKeyDown = event => {
    const { key } = event;
    const atStartOfInput = event.target.selectionStart === 0 && event.target.selectionEnd === 0;
    const hasSelectedContacts = this.selectedContacts.length > 0;

    if (selectionKeys.includes(key) && atStartOfInput && hasSelectedContacts) {
      if (this.highlightedContactId) {
        event.preventDefault();
        const contact = this.args.selectedContacts.find(c => c.id === this.highlightedContactId);
        if (contact && !this.isLocked(contact)) {
          this.args.onSelect?.(contact);
        }
        this.highlightedContactId = null;
      } else if (key === 'Backspace') {
        event.preventDefault();
        if (!this.isLocked(this.lastContact)) {
          this.highlightedContactId = this.lastContact.id;
        }
      }
      return;
    }

    this.highlightedContactId = null;
  };

  onInputBlur = () => {
    this.highlightedContactId = null;
  };

  // Separate path for when the user tabs to a tag and interacts directly.
  onTagKeyDown = (contact, event) => {
    const { key } = event;

    if (selectionKeys.includes(key)) {
      event.preventDefault();
      if (!this.isLocked(contact)) {
        this.args.onSelect?.(contact);
      }
    }
  };

  onTagClick = contact => {
    if (this.isLocked(contact)) return;
    this.args.onSelect?.(contact);
  };

  contactTagClass = contact => {
    return ['contact-tag', contact.isClient && 'client', this.isLocked(contact) && 'locked']
      .filter(Boolean)
      .join(' ');
  };

  <template>
    <div class="{{styles.component}} search-container" data-test-search-container>
      <label for="contactSearchInput" class="search-label">
        {{t "emc.contacts_page.search_input_label"}}
      </label>
      {{#if @multiSelect}}
        {{#each @selectedContacts as |contact|}}
          <Tag
            @label={{contact.name}}
            @onClose={{noop}}
            @small={{true}}
            @type="filter"
            aria-selected={{eq this.highlightedContactId contact.id}}
            class={{this.contactTagClass contact}}
            tabindex={{if (this.isLocked contact) "-1" "0"}}
            {{on "click" (fn this.onTagClick contact)}}
            {{on "keydown" (fn this.onTagKeyDown contact)}}
            data-test-contact-tag={{contact.id}}
            data-test-locked-contact-tag={{if (this.isLocked contact) ""}}
          />
        {{/each}}
      {{/if}}
      <TextInput
        class="search-input"
        @autofocus={{true}}
        @id="contactSearchInput"
        @value={{@search}}
        @onBlur={{this.onInputBlur}}
        @onInput={{@updateSearch}}
        @onKeyDown={{this.onInputKeyDown}}
        @placeholder={{this.searchPlaceholderText}}
        data-test-search-input
      />
      {{#if (and @search this.isSingleSelect)}}
        <button
          type="button"
          class="clear-search-button"
          aria-label={{t "emc.contacts_page.clear_search"}}
          {{on "click" @clearSearch}}
          data-test-clear-search-button
        >
          {{svgJar "x-mark"}}
        </button>
      {{/if}}
      <div id="search-help-text" class="visually-hidden">
        {{t "emc.contacts_page.search_help_text"}}
      </div>
    </div>
  </template>
}
